import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, HourglassEmpty } from '@mui/icons-material';

const ProcessingModal = ({
  open,
  onClose,
  title,
  status,
  progress,
  error,
  canClose = false
}) => {
  const getStatusIcon = () => {
    if (status === 'completed') return <CheckCircle color="success" />;
    if (status === 'failed') return <ErrorIcon color="error" />;
    if (status === 'processing') return <CircularProgress size={20} />;
    return <HourglassEmpty />;
  };

  const getProgressPercentage = () => {
    if (!progress || !progress.total) return 0;
    if (progress.total === 'all') return -1; // Indeterminate
    return (progress.completed / progress.total) * 100;
  };

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      disableEscapeKeyDown={!canClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {getStatusIcon()}
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {status === 'processing' && progress && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {progress.message || `Processing ${progress.completed || 0} of ${progress.total} images...`}
              </Typography>
              
              <LinearProgress
                variant={getProgressPercentage() === -1 ? "indeterminate" : "determinate"}
                value={getProgressPercentage()}
                sx={{ mt: 2, mb: 2 }}
              />

              {progress.details && (
                <Typography variant="caption" color="textSecondary">
                  {progress.details}
                </Typography>
              )}
            </>
          )}

          {status === 'completed' && (
            <Alert severity="success">
              Processing completed successfully!
              {progress && progress.total !== 'all' && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {progress.completed} images processed
                </Typography>
              )}
            </Alert>
          )}

          {status === 'submitting' && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography>Submitting job...</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={!canClose && status === 'processing'}
          variant={status === 'completed' || status === 'failed' ? 'contained' : 'text'}
        >
          {status === 'completed' || status === 'failed' ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessingModal;